import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useRemembered } from '../lib/remember';
import { PillMenu } from './PillMenu';
import { Segmented } from './Segmented';
import { useWide } from '../shell/hooks';

// Shelf-level controls: quiet view links, the fold arrow and the one shared shelf, Move ▾.

/**
 * Quiet view links (decisions doc, Oct 22): each names the *other* choice ("Show covers"
 * when showing cards). Remembered per shelf on this device; defaults come from the shelf.
 */
export type ShowAs = 'cards' | 'covers';
export type Arrange = 'one' | 'readtype';

export function useShelfView(shelf: string, defaults: { show: ShowAs; arrange: Arrange }) {
  const [show, setShow] = useRemembered<ShowAs>(`view:${shelf}:show`, defaults.show);
  const [arrange, setArrange] = useRemembered<Arrange>(`view:${shelf}:arrange`, defaults.arrange);
  return { show, setShow, arrange, setArrange };
}

/** A shelf's view: Cards | Covers and One order | By read type (Kristi, Oct 29: switches, on Reading and Reference alike). */
export function ViewLinks({
  view,
  oneOrderName = 'Reading order',
}: {
  view: ReturnType<typeof useShelfView>;
  /** What "one order" is called on this shelf (Finished: "Most recent first"). */
  oneOrderName?: string;
}) {
  return (
    <span class="view-links">
      <Segmented label="Show as" options={[['cards', 'Cards'], ['covers', 'Covers']]} value={view.show} onChange={view.setShow} />
      <Segmented label="Order" options={[['one', oneOrderName], ['readtype', 'By read type']]} value={view.arrange} onChange={view.setArrange} />
    </span>
  );
}

/** Fold arrow: ▸ folded, ▾ open. */
export function FoldArrow({ open }: { open: boolean }) {
  return (
    <span class={`fold${open ? ' open' : ''}`} aria-hidden="true">
      ▸
    </span>
  );
}

// Every Shelf on the page, so Collapse all / Expand all can fold or open them at once.
const shelfFolds = new Map<string, { open: boolean; set: (v: boolean) => void }>();
const foldWatchers = new Set<() => void>();
const foldsChanged = () => foldWatchers.forEach((w) => w());

/** Opens one shelf by its id (Reference's Jump to opens a folded group before scrolling to it). */
export function openShelf(id: string) {
  const f = shelfFolds.get(id);
  if (f && !f.open) f.set(true);
}

/**
 * Collapse all / Expand all (decisions doc, Oct 21): one button that flips between the two. It
 * says Collapse all while any shelf is open. Each shelf's own fold arrow still works.
 */
export function ShelfFoldAll() {
  const [, tick] = useState(0);
  useEffect(() => {
    const w = () => tick((n) => n + 1);
    foldWatchers.add(w);
    return () => void foldWatchers.delete(w);
  }, []);
  const all = [...shelfFolds.values()];
  if (!all.length) return null;
  const anyOpen = all.some((f) => f.open);
  return (
    <button type="button" class="btn-link shelf-foldall" onClick={() => all.forEach((f) => f.set(!anyOpen))}>
      {anyOpen ? 'Collapse all' : 'Expand all'}
    </button>
  );
}

/**
 * The one shelf component (backlog h023): header with fold arrow, name, count, description,
 * view links (only while open) and See all; then the body. Every shelf on every page uses it,
 * inside one page column, so order changes can't break alignment, background or folding.
 * Folds are remembered per device.
 */
export function Shelf({
  id,
  name,
  count,
  description,
  startOpen = true,
  view,
  seeAll,
  chips,
  pinHead,
  children,
}: {
  id: string;
  name: string;
  count?: string;
  description?: string;
  startOpen?: boolean;
  view?: ComponentChildren;
  seeAll?: ComponentChildren;
  chips?: ComponentChildren;
  /** The header and chips stay pinned while you scroll through this shelf (Reference panes, Kristi Oct 29). */
  pinHead?: boolean;
  children: ComponentChildren;
}) {
  const [open, setOpen] = useRemembered(`fold:${id}`, startOpen);
  const bodyId = `shelf-body-${id}`;
  // On a phone a pinned heading is one line: the name, its count and See all / Jump to; the view
  // switches and chips stay with the description and scroll away (check-up, Kristi Nov 2).
  const compact = !useWide() && Boolean(pinHead);
  useEffect(() => {
    shelfFolds.set(id, { open, set: setOpen });
    foldsChanged();
  });
  useEffect(
    () => () => {
      shelfFolds.delete(id);
      foldsChanged();
    },
    [id],
  );
  const head = (
    <div class="shelf-head">
      <h2 id={`shelf-h-${id}`} class="shelf-name">
        <button type="button" class="shelf-toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen(!open)}>
          <FoldArrow open={open} />
          {name}
        </button>
      </h2>
      {/* A heading's count in words after a dot (R7 N4, Kristi Nov 2): "Up Next · 14 books", "Cooking · 194 books". */}
      {count ? <span class="shelf-count">· {count}</span> : null}
      {open && view && !compact ? view : null}
      {seeAll ? <span class="shelf-see-all">{seeAll}</span> : null}
    </div>
  );
  return (
    <section class="shelf" aria-labelledby={`shelf-h-${id}`} data-shelf={id}>
      {pinHead ? (
        <div class={`shelf-pin${compact ? ' compact' : ''}`}>
          {head}
          {open && chips && !compact ? <div class="shelf-chips">{chips}</div> : null}
        </div>
      ) : (
        head
      )}
      {description ? <p class="shelf-desc">{description}</p> : null}
      {open && view && compact ? <div class="shelf-view">{view}</div> : null}
      {open ? (
        <div id={bodyId} class="shelf-body">
          {chips && (!pinHead || compact) ? <div class="shelf-chips">{chips}</div> : null}
          {children}
        </div>
      ) : null}
    </section>
  );
}

export interface MenuItem {
  label: string;
  onSelect: () => void;
  /** Starts a new group with a line above it. */
  divided?: boolean;
  /** Leaves this page (a trailing ›). */
  goes?: boolean;
  /** A quiet note at the right of the row ("asks why"). */
  note?: string;
  /** The shelf the book is on now: ✓ before it (R4). */
  current?: boolean;
  /** A small-capitals heading above this item ("Move to"). */
  heading?: string;
}

/**
 * Move ▾ (decisions doc › Move ▾ menu; consistency pass R4): the one pill drop-down, always the
 * last button in a card's action row; the menu opens below it on a laptop and as a sheet on a
 * phone, with ✓ before the shelf the book is on. The items are the shelves the book can go to,
 * in shelf order, plus shelf-specific ones. Shelves' cards and the Pieces page both use it.
 */
export function MoveMenu({ book, items }: { book: string; items: MenuItem[] }) {
  const current = items.findIndex((it) => it.current);
  return (
    <PillMenu
      label="Move"
      ariaLabel={`Move ${book}`}
      sheetTitle={`Move ${book}`}
      choices={items.map((it, i) => ({ value: i, label: `${it.label}${it.goes ? ' ›' : ''}`, note: it.note, divided: it.divided, heading: it.heading }))}
      current={current >= 0 ? current : null}
      onPick={(i) => items[i]?.onSelect()}
    />
  );
}
